import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GridSortModel } from "@mui/x-data-grid";

type SortDir = "asc" | "desc";

type UseSortModelConfig<T extends string> = {
  allowedSortFields: readonly T[];
};

type UseSortModelReturn<T extends string> = {
  sortModel: GridSortModel;
  handleSortModelChange: (model: GridSortModel) => void;
  orderBy: T | undefined;
  order: "ASC" | "DESC" | undefined;
};

/**
 * Custom hook to manage DataGrid sorting with URL sync
 * @param config - Configuration object with allowed sort fields
 * @returns Object containing sortModel state and handlers
 */
export const useSortModel = <T extends string>(
  config: UseSortModelConfig<T>,
): UseSortModelReturn<T> => {
  const { allowedSortFields } = config;
  const [searchParams, setSearchParams] = useSearchParams();

  const allowedFieldsSet = new Set<string>(allowedSortFields);

  // Type guards
  const isSortField = (v: unknown): v is T =>
    typeof v === "string" && allowedFieldsSet.has(v);

  const isSortDir = (v: unknown): v is SortDir => v === "asc" || v === "desc";

  // Initialize sort model from URL params
  const initialSortBy = (() => {
    const v = searchParams.get("orderBy") || "";
    return isSortField(v) ? v : ("" as const);
  })();

  const initialSortDir = (() => {
    const v = searchParams.get("order") || "";
    return isSortDir(v) ? v : ("" as const);
  })();

  const buildSortModel = (
    sortBy: T | "",
    sortDir: SortDir | "",
  ): GridSortModel =>
    sortBy && sortDir ? [{ field: sortBy, sort: sortDir }] : [];

  const [sortModel, setSortModel] = useState<GridSortModel>(
    buildSortModel(initialSortBy, initialSortDir),
  );

  /**
   * The field name to sort by, derived from the first item in the sort model.
   * Returns undefined if the field is not a valid sort field.
   * @type {SortableFields | undefined}
   */
  const orderBy = isSortField(sortModel[0]?.field)
    ? sortModel[0]!.field
    : undefined;

  /**
   * The sort order direction derived from the first sort model entry.
   * If a sort direction exists, converts it to uppercase.
   * @type {("ASC" | "DESC" | undefined)}
   * - "ASC": Ascending order
   * - "DESC": Descending order
   * - undefined: No sorting applied
   */
  const order = sortModel[0]?.sort
    ? (sortModel[0].sort.toUpperCase() as "ASC" | "DESC")
    : undefined;

  // Keep sortModel in sync with URL (e.g., back/forward navigation)
  useEffect(() => {
    const sortByRaw = searchParams.get("orderBy");
    const sortDirRaw = searchParams.get("order");
    const sortBy = isSortField(sortByRaw) ? sortByRaw : undefined;
    const sortDir = isSortDir(sortDirRaw) ? sortDirRaw : undefined;

    if (sortBy && sortDir) {
      const next: GridSortModel = [{ field: sortBy, sort: sortDir }];
      // Avoid state updates if same
      if (
        sortModel.length !== next.length ||
        sortModel[0]?.field !== next[0]?.field ||
        sortModel[0]?.sort !== next[0]?.sort
      ) {
        setSortModel(next);
      }
    } else if (sortModel.length) {
      setSortModel([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle sort model changes and sync to URL
  const handleSortModelChange = (model: GridSortModel) => {
    const first = model[0];
    const params = new URLSearchParams(searchParams);

    if (
      first &&
      isSortField(first.field) &&
      first.sort &&
      isSortDir(first.sort)
    ) {
      params.set("orderBy", first.field);
      params.set("order", first.sort);
      params.set("page", "1");
    } else {
      params.delete("orderBy");
      params.delete("order");
      params.set("page", "1");
    }

    setSearchParams(params, { replace: true });
    setSortModel(first && first.sort ? [first] : []);
  };

  return {
    sortModel,
    handleSortModelChange,
    orderBy,
    order,
  };
};
